import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Bookmark, ChevronDown, Plus, Trash2 } from "lucide-react";

export default function ViewsMenu({ currentState, currentLabel, outletCount, onLoad }) {
  const [views, setViews] = useState([]);
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const ref = useRef(null);

  async function load() {
    try { const { data } = await api.get("/views"); setViews(data); } catch (e) {}
  }
  useEffect(() => { load(); }, []);
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setNaming(false); } }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  async function save() {
    const n = name.trim();
    if (!n) return;
    const { data } = await api.post("/views", { name: n, state: currentState });
    setViews((v) => [data, ...v]);
    setName("");
    setNaming(false);
  }

  async function remove(id, e) {
    e.stopPropagation();
    await api.delete(`/views/${id}`);
    setViews((v) => v.filter((x) => x.id !== id));
  }

  const suggested = currentLabel ? `${outletCount === 0 ? "All stores" : `${outletCount} store${outletCount > 1 ? "s" : ""}`} · ${currentLabel}` : "";

  return (
    <div className="relative" ref={ref} data-testid="views-menu">
      <div className="bm-eyebrow mb-2">Views</div>
      <button
        data-testid="views-trigger"
        onClick={() => setOpen((v) => !v)}
        className="px-3 py-2 border border-[#0a0a0a] font-mono-bm text-sm flex items-center gap-2 hover:bg-[#0a0a0a]/5"
      >
        <Bookmark size={14} /> Saved views
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div data-testid="views-popover" className="absolute z-40 mt-2 w-[320px] right-0 bg-[#f5f2eb] border border-[#0a0a0a] shadow-[8px_8px_0_0_#0a0a0a] bm-popover-mobile">
          <div className="p-3 border-b border-[#0a0a0a]">
            {naming ? (
              <div className="space-y-2">
                <input
                  data-testid="view-name-input"
                  autoFocus
                  type="text"
                  value={name}
                  placeholder={suggested}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && save()}
                  className="w-full border border-[#0a0a0a] bg-transparent px-3 py-2 font-mono-bm text-sm focus:outline-none focus:border-[#c72c2c]"
                />
                <div className="flex gap-2">
                  <button data-testid="view-save-confirm" onClick={save} className="flex-1 bm-eyebrow px-3 py-2 bg-[#0a0a0a] text-[#f5f2eb] hover:bg-[#c72c2c]">Save</button>
                  <button onClick={() => { setNaming(false); setName(""); }} className="bm-eyebrow px-3 py-2 border border-[#0a0a0a] hover:bg-[#0a0a0a]/5">Cancel</button>
                </div>
              </div>
            ) : (
              <button
                data-testid="save-current-view"
                onClick={() => setNaming(true)}
                className="w-full flex items-center gap-2 bm-eyebrow px-3 py-2 border border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-[#f5f2eb]"
              >
                <Plus size={14} /> Save current view
              </button>
            )}
          </div>

          <div className="max-h-[280px] overflow-y-auto">
            {views.length === 0 && (
              <div className="p-4 text-sm text-neutral-500 font-mono-bm">No saved views yet.</div>
            )}
            {views.map((v) => (
              <button
                key={v.id}
                data-testid={`view-item-${v.id}`}
                onClick={() => { onLoad(v); setOpen(false); }}
                className="w-full text-left flex items-start justify-between gap-2 px-4 py-3 hover:bg-[#0a0a0a]/5 border-b border-[#0a0a0a]/20 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{v.name}</div>
                  <div className="bm-eyebrow text-neutral-500 mt-1">
                    {(v.state?.outletIds?.length ?? 0) === 0 ? "All stores" : `${v.state.outletIds.length} store${v.state.outletIds.length > 1 ? "s" : ""}`}
                    {" · "}
                    {v.state?.date?.tab === "date"
                      ? `${v.state.date.startDate} → ${v.state.date.endDate}`
                      : `${v.state?.date?.mode === "to_date" ? `${v.state?.date?.unit} to date` : v.state?.date?.mode === "previous" ? `Previous ${v.state?.date?.unit?.toLowerCase()}` : `Prev ${v.state?.date?.n} ${v.state?.date?.unit?.toLowerCase()}s`}`
                    }
                  </div>
                </div>
                <span
                  onClick={(e) => remove(v.id, e)}
                  data-testid={`view-delete-${v.id}`}
                  className="text-neutral-400 hover:text-[#c72c2c] p-1 flex-shrink-0 cursor-pointer"
                  role="button"
                >
                  <Trash2 size={14} />
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
